import { useMemo, useState, useEffect } from 'react';

import type { WhiteboardToken } from '@confluence/whiteboard-common/entry-points/whiteboard-shell-types.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useTokenService } from '@confluence/whiteboard-common/entry-points/useTokenService.ts';

import { useInitialUrl } from './useInitialUrl.ts';

interface Identity {
	accountId?: string | null;
	nickname?: string | null;
	picture?: string | null;
	email?: string | null;
	locale?: string | null;
}

export interface DatabaseBridgeDataProps {
	spaceKey?: string;
	contentId?: string;
	contentReferenceId?: string | null;
	spaceId?: string | null;
	spaceName?: string | null;
	title?: string | null;
	identity?: Identity | null;
	dataSecurityPolicyPageExport: boolean | null;
	refetch?: () => void;
	version?: string;
	isSmartLinkEmbed?: boolean;
	versionId: string | null;
	presentationMode?: string;
	bridgeClient?: string;
	isRovoParentProduct?: boolean;
}

export const useDatabaseBridgeData = ({
	spaceKey,
	contentId,
	contentReferenceId,
	spaceId,
	spaceName,
	title,
	identity,
	dataSecurityPolicyPageExport,
	refetch,
	version,
	isSmartLinkEmbed,
	versionId,
	presentationMode,
	bridgeClient,
	isRovoParentProduct,
}: DatabaseBridgeDataProps) => {
	const [token, setToken] = useState<WhiteboardToken | undefined>(undefined);
	const { activationId, userId, cloudId } = useSessionData();
	const initialUrl = useInitialUrl({
		version,
		isSmartLinkEmbed,
		versionId,
		presentationMode,
		bridgeClient,
		isRovoParentProduct,
	});

	const tokenService = useTokenService({
		tenantId: cloudId,
		space: {
			id: spaceId,
		},
		referenceId: contentReferenceId,
		shareToken: undefined,
		contentId: contentId || '',
		dataSecurityPolicyPageExport: dataSecurityPolicyPageExport || null,
		refetch,
		documentType: 'database',
	});

	useEffect(() => {
		tokenService.onTokenChange((newToken) => {
			setToken(newToken);
		});
	}, [tokenService]);

	const bridgeData = useMemo(
		() => ({
			spaceKey,
			contentId,
			cloudId,
			activationId,
			contentReferenceId,
			userId,
			spaceId: spaceId ?? undefined,
			spaceName: spaceName ?? undefined,
			title: title ?? undefined,
			identity: identity ?? undefined,
			token,
			versionId,
		}),
		[
			spaceKey,
			contentId,
			cloudId,
			activationId,
			contentReferenceId,
			userId,
			spaceId,
			spaceName,
			title,
			identity,
			token,
			versionId,
		],
	);

	return { bridgeData, initialUrl };
};
